import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { scansApi } from '../../api/scans';
import { DEFAULT_PARAMS, MIN_SEARCH_LENGTH, parseScansParams, toQueryString, updateParams } from './scansParams';

const SEARCH_DEBOUNCE_MS = 300;
export const scansListKey = (params) => ['scans', params];
export const scanDetailKey = (id) => ['scan', id];

const shouldRetry = (count, error) => (error?.status === 0 || error?.status >= 500) && count < 1;

/** Scans list, synced with ?status&search&userId&placeId&page&limit. */
export default function useScans() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { params, invalid } = useMemo(() => parseScansParams(searchParams), [searchParams]);

  const writeParams = useCallback(
    (next, { replace = false } = {}) => setSearchParams(toQueryString(next), { replace }),
    [setSearchParams],
  );

  useEffect(() => {
    if (invalid.length) writeParams(params, { replace: true });
  }, [invalid, params, writeParams]);

  const setParams = useCallback((changes) => writeParams(updateParams(params, changes)), [params, writeParams]);

  // --- debounced search box; 1 character means "no search" ---
  const [searchInput, setSearchInput] = useState(params.search);
  const lastApplied = useRef(params.search);
  useEffect(() => {
    if (params.search !== lastApplied.current) {
      lastApplied.current = params.search;
      setSearchInput(params.search);
    }
  }, [params.search]);

  useEffect(() => {
    const trimmed = searchInput.trim();
    const term = trimmed.length >= MIN_SEARCH_LENGTH ? trimmed : '';
    if (term === params.search) return undefined;
    const timer = setTimeout(() => {
      lastApplied.current = term;
      setParams({ search: term });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput, params.search, setParams]);

  const clearFilters = useCallback(() => {
    setSearchInput('');
    lastApplied.current = '';
    const { status, search, userId, placeId } = DEFAULT_PARAMS;
    setParams({ status, search, userId, placeId });
  }, [setParams]);

  const query = useQuery({
    queryKey: scansListKey(params),
    queryFn: ({ signal }) => scansApi.getScans(params, { signal }),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    retry: shouldRetry,
  });
  const { data, error, isPending, isFetching, isPlaceholderData, refetch } = query;

  // Past the last page: go to the last page that exists
  useEffect(() => {
    if (!data || isPlaceholderData) return;
    const lastPage = Math.max(1, data.pagination.totalPages);
    if (params.page > lastPage) writeParams({ ...params, page: lastPage }, { replace: true });
  }, [data, isPlaceholderData, params, writeParams]);

  // 400: show the message, reset the offending params once and retry
  const resetFor = useRef(null);
  useEffect(() => {
    if (error?.status !== 400) return;
    const key = toQueryString(params);
    if (resetFor.current === key) return;
    resetFor.current = key;
    toast.error(error.message);
    const fields = error.fieldErrors.map((e) => e.field).filter((f) => f in DEFAULT_PARAMS);
    const reset = Object.fromEntries((fields.length ? fields : Object.keys(DEFAULT_PARAMS)).map((f) => [f, DEFAULT_PARAMS[f]]));
    writeParams({ ...params, ...reset }, { replace: true });
  }, [error, params, writeParams]);

  return {
    params,
    setParams,
    searchInput,
    setSearchInput,
    clearFilters,
    scans: data?.scans ?? [],
    statusCounts: data?.statusCounts ?? null,
    pagination: data?.pagination ?? null,
    isInitialLoading: isPending && !data,
    isRefreshing: isFetching && isPlaceholderData,
    error: data && !isPlaceholderData ? null : error,
    refetch,
  };
}
